<script setup lang="ts">
/**
 * Narratives sub-nav — the left list panel of the Narratives workspace,
 * mirroring the Dialogues sidebar (DialogSessionList). Lists the matter's
 * narratives (from the stub narratives API via narrativesStore); clicking one
 * navigates to its detail page. Each row has a hover-revealed kebab menu with
 * Rename and Delete, which open dialogs mirroring the dialog-session rename /
 * delete flow (confirm dialog, in-flight progress bar, success/failure toast).
 * The action row (a "new narrative" + on the left, a "search" toggle to its
 * right) and the search box reuse the same `.sessions-*` styles as the
 * Dialogues sidebar for visual parity. Search filters the list by name
 * client-side.
 */

import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNarrativesStore } from '../../stores/narrativesStore';

const { t } = useI18n();
const store = useNarrativesStore();

// Parent bumps this (e.g. from the collapsed rail) to ask us to open + focus
// the search box. Mirrors DialogSessionList's `searchTrigger`.
const props = defineProps<{ searchTrigger?: number }>();

// The add button asks the page to create + open a narrative; clicking a row
// asks the page to navigate to it.
const emit = defineEmits<{
    (e: 'new-narrative'): void;
    (e: 'select', narrativeId: string): void;
}>();

const searchOpen = ref(false);
const searchQuery = ref('');
const searchInput = ref<HTMLInputElement | null>(null);

const openSearch = () => {
    searchOpen.value = true;
    nextTick(() => searchInput.value?.focus());
};

const closeSearch = () => {
    searchOpen.value = false;
    searchQuery.value = '';
};

const toggleSearch = () => {
    if (searchOpen.value) {
        closeSearch();
    } else {
        openSearch();
    }
};

watch(
    () => props.searchTrigger,
    (val) => {
        if (val !== undefined && val > 0) openSearch();
    },
    { immediate: true },
);

const normalizedQuery = computed(() => searchQuery.value.trim().toLowerCase());

const filteredNarratives = computed(() => {
    const all = store.narratives;
    if (!normalizedQuery.value) return all;
    return all.filter((n) => n.title.toLowerCase().includes(normalizedQuery.value));
});

const onAddNarrative = () => {
    emit('new-narrative');
};

const onSelect = (narrativeId: string) => {
    emit('select', narrativeId);
};

// ── Context menu + rename/delete (mirrors DialogSessionList) ────────────────
const contextMenuNarrativeId = ref<string | null>(null);
const contextMenuPosition = ref({ x: 0, y: 0 });

const showDeleteConfirm = ref(false);
const narrativeToDelete = ref<string | null>(null);
const deleting = ref(false);

const showRenamePrompt = ref(false);
const narrativeToRename = ref<string | null>(null);
const renameNewName = ref('');
const renaming = ref(false);

const toastMessage = ref('');
const showToast = ref(false);

const nameFor = (narrativeId: string | null) =>
    store.narratives.find((n) => n.id === narrativeId)?.title ?? '';

const deleteMessage = computed(() =>
    t('narratives.deleteModal.message', { name: nameFor(narrativeToDelete.value) }),
);

const showContextMenu = (event: MouseEvent, narrativeId: string) => {
    event.stopPropagation();
    contextMenuNarrativeId.value = narrativeId;
    contextMenuPosition.value = { x: event.clientX, y: event.clientY };
};

const closeContextMenu = () => {
    contextMenuNarrativeId.value = null;
};

const displayToast = (message: string) => {
    toastMessage.value = message;
    showToast.value = true;
    setTimeout(() => {
        showToast.value = false;
    }, 3000);
};

// ── Delete ──────────────────────────────────────────────────────────────
const confirmDelete = (narrativeId: string) => {
    narrativeToDelete.value = narrativeId;
    showDeleteConfirm.value = true;
    closeContextMenu();
};

const cancelDelete = () => {
    if (deleting.value) return;
    showDeleteConfirm.value = false;
    narrativeToDelete.value = null;
};

const performDelete = async () => {
    if (!narrativeToDelete.value || deleting.value) return;
    deleting.value = true;
    try {
        await store.deleteNarrative(narrativeToDelete.value);
        showDeleteConfirm.value = false;
        narrativeToDelete.value = null;
        displayToast(t('narratives.toast.deleteSuccess'));
    } catch {
        displayToast(t('narratives.toast.deleteFailed'));
    } finally {
        deleting.value = false;
    }
};

// ── Rename ──────────────────────────────────────────────────────────────
const startRename = (narrativeId: string) => {
    narrativeToRename.value = narrativeId;
    renameNewName.value = nameFor(narrativeId);
    showRenamePrompt.value = true;
    closeContextMenu();
};

const cancelRename = () => {
    if (renaming.value) return;
    showRenamePrompt.value = false;
    narrativeToRename.value = null;
    renameNewName.value = '';
};

const performRename = async () => {
    if (!renameNewName.value.trim() || !narrativeToRename.value || renaming.value) {
        return;
    }
    renaming.value = true;
    try {
        await store.renameNarrative(narrativeToRename.value, renameNewName.value.trim());
        showRenamePrompt.value = false;
        narrativeToRename.value = null;
        renameNewName.value = '';
        displayToast(t('narratives.toast.renameSuccess'));
    } catch {
        displayToast(t('narratives.toast.renameFailed'));
    } finally {
        renaming.value = false;
    }
};
</script>

<template>
    <div class="narrative-list">
        <div class="sessions-actions">
            <div class="sessions-actions-row">
                <button
                    type="button"
                    class="sessions-action-btn"
                    :title="t('narratives.newNarrative')"
                    :aria-label="t('narratives.newNarrative')"
                    @click="onAddNarrative"
                >
                    <span class="material-icons">add</span>
                </button>
                <button
                    type="button"
                    class="sessions-action-btn"
                    :class="{ 'sessions-action-btn-active': searchOpen }"
                    :title="t('narratives.search')"
                    :aria-label="t('narratives.search')"
                    :aria-pressed="searchOpen"
                    @click="toggleSearch"
                >
                    <span class="material-icons">search</span>
                </button>
            </div>
            <div v-if="searchOpen" class="sessions-search">
                <span class="material-icons sessions-search-icon">search</span>
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    class="sessions-search-input"
                    :placeholder="t('narratives.searchPlaceholder')"
                    @keyup.esc="closeSearch"
                />
                <button
                    v-if="searchQuery"
                    type="button"
                    class="sessions-search-clear"
                    :title="t('narratives.clearSearch')"
                    :aria-label="t('narratives.clearSearch')"
                    @click="searchQuery = ''"
                >
                    <span class="material-icons">close</span>
                </button>
            </div>
        </div>

        <div class="sessions-section">
            <h3 class="sessions-heading">{{ t('narratives.allNarratives') }}</h3>

            <div v-if="store.loading && store.narratives.length === 0" class="loading-small">
                {{ t('common.loading') }}
            </div>
            <div v-else-if="store.error" class="error-small">{{ store.error }}</div>
            <div v-else-if="filteredNarratives.length === 0" class="sessions-empty">
                {{ normalizedQuery ? t('narratives.noMatches') : t('narratives.empty') }}
            </div>

            <div v-else class="sessions-list" @click="closeContextMenu">
                <div
                    v-for="narrative in filteredNarratives"
                    :key="narrative.id"
                    class="session-item"
                    @click="onSelect(narrative.id)"
                >
                    <div class="session-content">
                        <div class="session-name">{{ narrative.title }}</div>
                    </div>
                    <button
                        class="session-menu-button"
                        :aria-label="t('narratives.menu.aria')"
                        @click="showContextMenu($event, narrative.id)"
                    >
                        ⋮
                    </button>
                </div>
            </div>
        </div>

        <div
            v-if="contextMenuNarrativeId"
            class="context-menu"
            :style="{ top: contextMenuPosition.y + 'px', left: contextMenuPosition.x + 'px' }"
            @click.stop
        >
            <div
                class="context-menu-item"
                @click="contextMenuNarrativeId && startRename(contextMenuNarrativeId)"
            >
                {{ t('narratives.menu.rename') }}
            </div>
            <div
                class="context-menu-item"
                @click="contextMenuNarrativeId && confirmDelete(contextMenuNarrativeId)"
            >
                {{ t('narratives.menu.delete') }}
            </div>
        </div>

        <div
            v-if="contextMenuNarrativeId"
            class="context-menu-backdrop"
            @click="closeContextMenu"
        ></div>

        <div v-if="showDeleteConfirm" class="modal-overlay" @click="cancelDelete">
            <div class="modal-content" @click.stop>
                <div class="modal-title">{{ t('narratives.deleteModal.title') }}</div>
                <p>{{ deleteMessage }}</p>
                <div class="modal-buttons">
                    <button class="modal-button-cancel" :disabled="deleting" @click="cancelDelete">
                        {{ t('common.cancel') }}
                    </button>
                    <button class="modal-button-delete" :disabled="deleting" @click="performDelete">
                        {{ t('common.delete') }}
                    </button>
                </div>
                <div
                    v-if="deleting"
                    class="modal-progress-bar"
                    :aria-label="t('narratives.deleteModal.progress')"
                ></div>
            </div>
        </div>

        <div v-if="showRenamePrompt" class="modal-overlay" @click="cancelRename">
            <div class="modal-content" @click.stop>
                <div class="modal-title">{{ t('narratives.renameModal.title') }}</div>
                <input
                    v-model="renameNewName"
                    type="text"
                    :placeholder="t('narratives.renameModal.placeholder')"
                    class="modal-input"
                    :disabled="renaming"
                    @keyup.enter="performRename"
                    @keyup.esc="cancelRename"
                />
                <div class="modal-buttons">
                    <button class="modal-button-cancel" :disabled="renaming" @click="cancelRename">
                        {{ t('common.cancel') }}
                    </button>
                    <button class="modal-button-create" :disabled="renaming" @click="performRename">
                        {{
                            renaming
                                ? t('narratives.renameModal.saving')
                                : t('narratives.renameModal.rename')
                        }}
                    </button>
                </div>
                <div
                    v-if="renaming"
                    class="modal-progress-bar"
                    :aria-label="t('narratives.renameModal.progress')"
                ></div>
            </div>
        </div>

        <div v-if="showToast" class="toast-notification">
            {{ toastMessage }}
        </div>
    </div>
</template>
